import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import MiniCart from './Components/MiniCart';
import QuickSearch from './Components/QuickSearch';
import Navigation from './Components/Navigation';
import FacetedSearch from './Components/FacetedSearch';
import FacetedSearchCompactContainer from './Components/FacetedSearchCompactContainer';
import DynamicComponent from './Components/DynamicComponent';
import MessageBannerContainer from './Components/MessageBannerContainer';
import LoadingSpinner from './Components/LoadingSpinner';
import { setArticleNumberToFetchPrices } from './Actions/CustomerPrices.action';

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(
    app,
    preloadState,
    composeWithDevTools(applyMiddleware(thunk, historyMiddleware))
);

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
    if (!store.asyncReducers[key]) {
        store.asyncReducers[key] = asyncReducer;
        store.replaceReducer(createReducer(store.asyncReducers));
    }
};

window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
    registeredContainers.push(container);
    ReactDOM.render(element, container, callback);
};

window.onpagehide = () => {
    // make sure components are unmounted, redux's listener are unsubscribed
    // to fix issue with iframe navigation in IE
    registeredContainers.forEach((container) => {
        ReactDOM.unmountComponentAtNode(container);
    });
};

const bootstrapComponents = () => {
    if (document.getElementById('miniCart')) {
        renderReact(
            <Provider store={store}>
                <MiniCart />
            </Provider>,
            document.getElementById('miniCart')
        );
    }
    if (document.getElementById('quickSearch')) {
        renderReact(
            <Provider store={store}>
                <QuickSearch />
            </Provider>,
            document.getElementById('quickSearch')
        );
    }
    if (document.getElementById('quickBigSearch')) {
        const QuickBigSearch = DynamicComponent({
            loader: () => import('./Components/QuickBigSearch'),
        });

        renderReact(
            <Provider store={store}>
                <QuickBigSearch />
            </Provider>,
            document.getElementById('quickBigSearch')
        );
    }
    if (document.getElementById('navbar')) {
        renderReact(
            <Provider store={store}>
                <Navigation />
            </Provider>,
            document.getElementById('navbar')
        );
    }
    if (document.getElementById('facetedSearch')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearch />
            </Provider>,
            document.getElementById('facetedSearch')
        );
    }
    if (document.getElementById('facetedSearchCompact')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchCompactContainer />
            </Provider>,
            document.getElementById('facetedSearchCompact')
        );
    }
    if (document.getElementById('global-spinner')) {
        renderReact(
            <Provider store={store}>
                <LoadingSpinner />
            </Provider>,
            document.getElementById('global-spinner')
        );
    }

    if (document.getElementById('myPagePersons')) {
        const PersonList = DynamicComponent({
            loader: () => import('./Components/PersonListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <PersonList />
            </Provider>,
            document.getElementById('myPagePersons')
        );
    }
    if (document.getElementById('myPageAddresses')) {
        const AddressList = DynamicComponent({
            loader: () => import('./Components/AddressListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <AddressList />
            </Provider>,
            document.getElementById('myPageAddresses')
        );
    }
    if (document.getElementById('checkout')) {
        const Checkout = DynamicComponent({
            loader: () => import('./Components/Checkout'),
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            document.getElementById('checkout')
        );
    }
    if (document.getElementById('lightBoxImages')) {
        const LightboxImages = DynamicComponent({
            loader: () => import('./Components/LightboxImages'),
        });
        import('./Reducers/LightboxImages.reducer').then(
            ({ lightboxImages }) => {
                store.injectReducer('lightboxImages', lightboxImages);
                const rootElement = document.getElementById('lightBoxImages');
                const images = Array.from(
                    rootElement.querySelectorAll('template')
                ).map((img) => ({
                    html: img.innerHTML,
                    src: img.dataset.src,
                }));
                renderReact(
                    <Provider store={store}>
                        <LightboxImages images={images} />
                    </Provider>,
                    document.getElementById('lightBoxImages')
                );
            }
        );
    }

    if (document.querySelectorAll('.slider').length > 0) {
        const Slider = DynamicComponent({
            loader: () => import('./Components/Slider'),
        });
        Array.from(document.querySelectorAll('.slider')).forEach(
            (slider, index) => {
                const values = [...slider.querySelectorAll('template')].map(
                    (block) => {
                        return {
                            html: block.innerHTML,
                        };
                    }
                );
                if (values.length > 0) {
                    renderReact(<Slider values={values} />, slider);
                }
            }
        );
    }

    if (document.querySelectorAll('quantity-input').length > 0) {
        const QuantityInput = DynamicComponent({
            loader: () => import('./Components/QuantityInput'),
        });
        Array.from(document.querySelectorAll('quantity-input')).forEach(
            (quantityInput) => {
                const {
                    variantid,
                    quantityfieldid,
                    min,
                    max,
                    maxlength,
                    placeholder,
                    value,
                    classname,
                } = quantityInput.dataset;

                renderReact(
                    <Provider store={store}>
                        <QuantityInput
                            {...{
                                variantId: variantid,
                                quantityFieldId: quantityfieldid,
                                min,
                                max,
                                maxLength: maxlength,
                                placeholder,
                                value,
                                className: classname,
                            }}
                        />
                    </Provider>,
                    quantityInput
                );
            }
        );
    }

  if (document.getElementById('QuantityCheckDialog')) {
    const QuantityCheckDialog = DynamicComponent({
      loader: () => import('./Components/QuantityCheckDialog'),
    });

    renderReact(
      <Provider store={store}>
        <QuantityCheckDialog />
      </Provider>,
      document.getElementById('QuantityCheckDialog')
    );
  }

    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./Components/BuyButton'),
        });
    Array.from(document.querySelectorAll('buy-button')).forEach((button) => {
                const {
                    articleNumber,
                    quantityFieldId,
                    href,
                    cssClass,
                    label,
                } = button.dataset;
                renderReact(
                    <Provider store={store}>
                        <BuyButton
                            {...{
                                label,
                                articleNumber,
                                quantityFieldId,
                                href,
                                cssClass,
                                minimumLoadingTime: 1
                            }}
                        />
                    </Provider>,
                    button
                );
            }
        );
    }

    if (document.querySelectorAll('buy-multiple-button').length > 0) {
        const BuyMultipleButton = DynamicComponent({
            loader: () => import('./Components/BuyMultipleButton'),
        })
        Array.from(document.querySelectorAll('buy-multiple-button')).forEach(
            (button) => {
                const {
                    mode,
                } = button.dataset;
                renderReact(
                    <Provider store={store}>
                        <BuyMultipleButton mode={mode} />
                    </Provider>,
                    button
                )
            }
        )
    }


    if (document.querySelectorAll('favourite-button').length > 0) {
        const FavouriteButton = DynamicComponent({
            loader: () => import('./Components/Favourites/FavouriteButton'),
        });
        Array.from(document.querySelectorAll('favourite-button')).forEach(
            (button) => {
                const {
                    variantSystemId,
                    cssClass
                } = button.dataset;
                renderReact(
                    <Provider store={store}>
                        <FavouriteButton
                            {...{
                                variantSystemId,
                                cssClass,
                                mode: 'list'
                            }}
                        />
                    </Provider>,
                    button
                );
            }
        );
    }

    if (document.querySelectorAll('smd-select').length > 0) {
        const SmdSelect = DynamicComponent({
            loader: () => import('./Components/SmdSelect'),
        });
        Array.from(document.querySelectorAll('smd-select')).forEach(
            (select) => {
                const {
                    navigation
                } = select.dataset;
                renderReact(
                    <Provider store={store}>
                        <SmdSelect
                            {...{
                                navigation: JSON.parse(navigation)
                            }}
                        />
                    </Provider>,
                    select
                );
            }
        );
    }

    if (document.getElementById('orderHistoryPage')) {
        const OrderList = DynamicComponent({
            loader: () => import('./Components/OrderHistoryListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <OrderList />
            </Provider>,
            document.getElementById('orderHistoryPage')
        );
    }

    if (document.getElementById('invoiceHistoryPage')) {
        const InvoiceList = DynamicComponent({
            loader: () => import('./Components/InvoiceHistoryListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <InvoiceList />
            </Provider>,
            document.getElementById('invoiceHistoryPage')
        );
    }

    if (document.getElementById('favouriteListsPage')) {
        const FavouriteLists = DynamicComponent({
            loader: () => import('./Components/Favourites/FavouriteListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <FavouriteLists />
            </Provider>,
            document.getElementById('favouriteListsPage')
        );
    }

    const messageBannersEl = document.getElementById("messageBanners");
    if (messageBannersEl) {
        renderReact(
            <Provider store={store}>
                <MessageBannerContainer stateKey='messageBanners' usePadding={true} />
            </Provider>,
            messageBannersEl
        );
    }

    const dropzoneEl = document.getElementById('dropzone');
    if (dropzoneEl) {
        const DropZone = DynamicComponent({
            loader: () => import('./Components/DropZone'),
        });

        renderReact(
            <Provider store={store}>
                <DropZone />
            </Provider>,
            dropzoneEl
        );
    }

    if (document.getElementById('qa-accordion')) {
        import('./Components/QaAccordion')
            .then(qaAccordion => {
                qaAccordion.qaAccordionInit();
            });
    }

    if (document.getElementById('price-list-download')) {
        const DownloadButtons = DynamicComponent({
            loader: () => import('./Components/PriceListDownloadButtons'),
        });

        renderReact(
            <Provider store={store}>
                <DownloadButtons />
            </Provider>,
            document.getElementById('price-list-download')
        );
    }

    if (document.getElementById('product-view-option')) {
        const ProductViewOptions = DynamicComponent({
            loader: () => import('./Components/ProductViewOptions')
        });

        renderReact(
            <Provider store={store}>
                <ProductViewOptions />
            </Provider>,
            document.getElementById('product-view-option')
        );
    }

    const productListItemToggles = document.querySelectorAll('.js-product-list-item-toggle');
    if (productListItemToggles.length > 0) {
        const ProductListItemToggleButton = DynamicComponent({
            loader: () => import('./Components/ProductListItemToggleButton')
        });

        Array.from(productListItemToggles).forEach(
            (button) => {
                renderReact(
                    <Provider store={store}>
                        <ProductListItemToggleButton/>
                    </Provider>,
                    button
                );
            }
        );
    }

    const customerPrices = document.querySelectorAll('customer-prices');
    if (customerPrices.length > 0) {
      const CustomerPrices = DynamicComponent({
        loader: () => import('./Components/CustomerPrices'),
      });
  
      Array.from(customerPrices).forEach((customerPrice, index, array) => {
        const { variantSystemId, mode } = customerPrice.dataset;
  
        store.dispatch(setArticleNumberToFetchPrices(variantSystemId));  
        renderReact(
          <Provider store={store}>
            <CustomerPrices variantSystemId={variantSystemId} mode={mode} fetchPrices={index +1 === array.length}/>
          </Provider>,
          customerPrice
        );
      });
    }

    var selectOrganizationContainer = document.getElementById('js-selectOrganizationContainer');
    if (selectOrganizationContainer) {
        import('./Reducers/SelectOrganization.reducer').then(({ selectOrganization }) => {
            store.injectReducer('selectOrganization', selectOrganization);
            const SelectOrganizationContainer = DynamicComponent({
                loader: () => import('./Components/SelectOrganizationContainer'),
            });
            const defaultName = selectOrganizationContainer.dataset.orgName;
            const isSelected = selectOrganizationContainer.dataset.orgIsSelected == "True" ? true : false;
            renderReact(
                <Provider store={store}>
                    <SelectOrganizationContainer defaultName={defaultName} isSelected={isSelected} />
                </Provider>,
                selectOrganizationContainer
            );
        });
    }

    var myPageDetails = document.getElementById('js-myPageUserDetails');
    if (myPageDetails) {
        const MyPageUserDetails = DynamicComponent({
            loader: () => import('./Components/MyPageUserDetails'),
        });
        renderReact(
            <Provider store={store}>
                <MyPageUserDetails />
            </Provider>,
            myPageDetails
        );
    }

    var adverts = document.getElementsByClassName('js-advert');
    if (adverts.length > 0) {
        Array.from(adverts).forEach(
            (advert) => {
                advert.addEventListener('click', (event) => {
                    const url = event.currentTarget.getAttribute('href');
                    if (event.currentTarget.dataset.position && event.currentTarget.dataset.pagetype && url){
                        gtag('event', 'ad_click', {
                            'ad_position': 'Annonsplats ' + event.currentTarget.dataset.position,
                            'destination_url': window.location.protocol + "//" + window.location.hostname + url,
                            'page_type': event.currentTarget.dataset.pagetype
                        })
                    }
                })
            }
        );
    }

    const mypages = document.getElementById('mypages');
    if (mypages) {
        const MyPages = DynamicComponent({
            loader: () => import('./Components/MyPages'),
        });
        const {
            data
        } = mypages.dataset;
        renderReact(
            <Provider store={store}>
                <MyPages data={JSON.parse(data)}/>
            </Provider>,
            mypages
        );
    }

    const barcodescannerHtml5 = document.getElementById('barcodescanner');
    if (barcodescannerHtml5) {
        const Html5Scanner = DynamicComponent({
            loader: () => import('./Components/Html5Scanner'),
        });
        renderReact(
            <Provider store={store}>
                <Html5Scanner />
            </Provider>,
            barcodescannerHtml5
        );
    }
};

bootstrapComponents();
